import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { useStartProject } from "@/lib/startProjectContext";
import StartProjectForm from "@/components/StartProjectForm";

/**
 * StartProjectModal — full-width premium overlay with backdrop blur.
 * Global — rendered once at the app root by StartProjectProvider consumer.
 * Opens/closes via useStartProject().open()/close().
 */
export const StartProjectModal = () => {
  const { isOpen, close } = useStartProject();
  const modalRef = useRef(null);
  const openerRef = useRef(null);

  // Restore focus to the trigger element on close.
  useEffect(() => {
    if (isOpen) {
      openerRef.current = document.activeElement;
    } else if (openerRef.current && typeof openerRef.current.focus === "function") {
      openerRef.current.focus();
    }
  }, [isOpen]);

  // Trap focus inside the modal.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== "Tab") return;
      const container = modalRef.current;
      if (!container) return;
      const focusable = container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="sp-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sp-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
      data-testid="start-project-modal"
    >
      <div className="sp-modal" ref={modalRef}>
        <div className="sp-modal-head">
          <div>
            <p className="sp-modal-eyebrow">Start your project</p>
            <h2 className="sp-modal-title" id="sp-modal-title">
              Tell us about the piece you're bringing to market.
            </h2>
          </div>
          <button
            type="button"
            className="sp-modal-close"
            onClick={close}
            aria-label="Close start-your-project form"
            data-testid="start-project-close"
          >
            <X size={20} strokeWidth={1.4} aria-hidden />
          </button>
        </div>
        <StartProjectForm variant="modal" />
      </div>
    </div>
  );
};

export default StartProjectModal;
